'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetTradesRow, hashDiagnosticEntity, useGetTrades } from '@morfi/backend-sdk-v2/client';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import type { MarketChartTradeTapeEvent } from '@/components/chart/market-chart';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { selectActiveTradeRows } from '@/lib/chart/active-trade-rows';
import { LIVE_WINDOW_SECONDS } from '@/lib/chart/chart-factory';
import { mapTradeToAnnotation } from '@/lib/chart/sdk-mapping';
import { useFormattedCurrency } from '@/lib/currency';
import { MARKET_TRADES_TAPE_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
export function useWinnerTradeTape(t0) {
  const $ = _c(32);
  const {
    marketPubkey,
    outcomeMint,
    originalOutcomeIndex
  } = t0;
  const [from] = useState(_temp);
  const t1 = String(originalOutcomeIndex);
  let t2;
  if ($[0] !== outcomeMint) {
    t2 = [outcomeMint];
    $[0] = outcomeMint;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [...MARKET_TRADES_TAPE_FIELDS];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== from || $[4] !== marketPubkey || $[5] !== t1 || $[6] !== t2) {
    t4 = {
      market: marketPubkey,
      outcomeIndex: t1,
      mints: t2,
      from,
      sortBy: "timestamp",
      sortOrder: "desc",
      fields: t3
    };
    $[3] = from;
    $[4] = marketPubkey;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      eagerSnapshot: true
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const query = useGetTrades(t4, t5);
  const rows = useLiveListData(query.queryKey, query.rows, query.entityQueryKey, query.projectEntityRow);
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    resolvedTheme
  } = useTheme();
  let t6;
  if ($[9] !== resolvedTheme) {
    t6 = resolveBrandColor(resolvedTheme);
    $[9] = resolvedTheme;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const color = t6;
  let droppedCount;
  let tape;
  if ($[11] !== color || $[12] !== formatD6 || $[13] !== originalOutcomeIndex || $[14] !== query.liveSinceMountIds || $[15] !== rows) {
    tape = [];
    droppedCount = 0;
    for (const row of selectActiveTradeRows(rows, new Set(query.liveSinceMountIds))) {
      if (row.outcomeIndex !== originalOutcomeIndex) {
        continue;
      }
      const trade = mapTradeToAnnotation(row, 0);
      if (trade.side === undefined || !Number.isFinite(trade.usdcAmount) || trade.usdcAmount <= 0) {
        droppedCount = droppedCount + 1;
        droppedCount;
        continue;
      }
      tape.push({
        id: row.eventId,
        side: trade.side,
        size: trade.usdcAmount,
        label: `${trade.side === "buy" ? "+" : "-"}${formatD6(String(row.usdcAmount), "compact")}`,
        color
      });
    }
    $[11] = color;
    $[12] = formatD6;
    $[13] = originalOutcomeIndex;
    $[14] = query.liveSinceMountIds;
    $[15] = rows;
    $[16] = droppedCount;
    $[17] = tape;
  } else {
    droppedCount = $[16];
    tape = $[17];
  }
  let t7;
  if ($[18] !== marketPubkey) {
    t7 = hashDiagnosticEntity(marketPubkey);
    $[18] = marketPubkey;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const marketHash = t7;
  let t8;
  if ($[20] !== outcomeMint) {
    t8 = hashDiagnosticEntity(outcomeMint);
    $[20] = outcomeMint;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const mintHash = t8;
  const errorClass = query.isError ? query.error?.name ?? "unknown" : null;
  let t9;
  if ($[22] !== droppedCount || $[23] !== errorClass || $[24] !== marketHash || $[25] !== mintHash) {
    t9 = () => {
      if (errorClass !== null) {
        logger.warn("winner_detail.trade_tape_unavailable", {
          errorClass,
          marketHash,
          mintHash
        });
      }
      if (droppedCount > 0) {
        logger.warn("winner_detail.trade_tape_row_dropped", {
          droppedCount,
          marketHash,
          mintHash
        });
      }
    };
    $[22] = droppedCount;
    $[23] = errorClass;
    $[24] = marketHash;
    $[25] = mintHash;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== droppedCount || $[28] !== errorClass || $[29] !== marketHash || $[30] !== mintHash) {
    t10 = [errorClass, droppedCount, marketHash, mintHash];
    $[27] = droppedCount;
    $[28] = errorClass;
    $[29] = marketHash;
    $[30] = mintHash;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  useEffect(t9, t10);
  return tape;
}
function _temp() {
  return new Date(Date.now() - LIVE_WINDOW_SECONDS * 1000).toISOString();
}
function resolveBrandColor(_theme: string | undefined): string {
  if (typeof document === 'undefined') return '#3b82f6';
  return getComputedStyle(document.documentElement).getPropertyValue('--foreground-brand').trim() || '#3b82f6';
}